import React, { useState, useCallback, useEffect, useRef } from "react";
// Chakra imports
import {
  Box,
  Button,
  Flex,
  Input,
  InputGroup,
  InputLeftAddon,
  useColorMode,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { BeatLoader } from "react-spinners";
import { format } from "date-fns";
import Select from "react-select";
import chroma from "chroma-js";
import { useSnackbar } from "notistack";
import { Card } from "@mantine/core";
import CardBody from "components/Card/CardBody";
// AWS SDK
import AWS from "aws-sdk";
// api calls
import { getClients } from "api/requests/getClients.js";
import { updateClient } from "api/requests/updateClient";

function S3Upload() {
  const initialFocusRef = useRef();
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadedURLs, setUploadedURLs] = useState([]);
  const bgColor = useColorModeValue("white", "#4A5058");
  // AWS S3 Info
  const [folderPath, setFolderPath] = useState("Please Select a Client");
  const S3_BUCKET_NAME = "dealer-alchemist";
  const AWS_REGION = "us-east-1";
  const S3_ACCESS_KEY_ID = process.env.REACT_APP_S3_ACCESS_KEY_ID;
  const S3_SECRET_ACCESS_KEY = process.env.REACT_APP_S3_SECRET_ACCESS_KEY;

  // Chakra color mode
  const { colorMode } = useColorMode();
  const toast = useToast();
  const [titles, setTitles] = useState([]);
  const [newTitles, setNewTitles] = useState("empty");
  const [isLoading, setIsLoading] = useState(false);
  const [options, setOptions] = useState("empty");
  const [value, setValue] = useState(null);
  const date = format(new Date(), "MMyy");

  const newOption = newTitles === "empty" ? titles : newTitles;

  const [clients, setClients] = useState([]);
  const [client, setClient] = useState();

  const bgCard = useColorModeValue(
    "gray.200",
    "linear-gradient(81.62deg, #343A40 2.25%, #212529 79.87%)"
  );

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  useEffect(() => {
    getClients().then((data) => {
      setClients(data),
        setTitles(
          data.map((titles) => ({
            value: titles.value,
            label: titles.label,
          }))
        );
    });
  }, []);

  const colorStyles = {
    control: (styles) => ({
      ...styles,
      backgroundColor: useColorModeValue("white", "#495057"),
      padding: "4px",
      borderRadius: "4px",
      border: "none",
    }),
    option: (styles, { isFocused, isSelected }) => ({
      ...styles,
      backgroundColor:
        isSelected && colorMode === "dark"
          ? "#3182ce"
          : isFocused
          ? "#3182ce"
          : undefined,
      color: useColorModeValue("gray.600", "white"),
    }),
    singleValue: (styles, { isFocused }) => {
      return {
        ...styles,
        color: useColorModeValue("gray.600", "white"),
        backgroundColor:
          colorMode === "dark" ? "#3182ce" : isFocused ? "#d0dcfb" : "#3182ce",
        padding: "5px",
        borderRadius: "4px",
        fontSize: "85%",
        width: "fit-content",
      };
    },
    menu: (styles) => ({
      ...styles,
      backgroundColor: useColorModeValue("white", "#495057"),
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  };

  const createOption = (label) => ({
    value: label.replace(/\W/g, ""),
    label,
  });

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);
    // Sort files by name
    files.sort((a, b) => a.name.localeCompare(b.name));
    setSelectedFiles(files);
  };

  const handleDrop = useCallback((event) => {
    event.preventDefault();

    // Check if files were dropped directly or selected from file input
    const droppedFiles =
      event.dataTransfer.files.length > 0
        ? event.dataTransfer.files
        : event.target.files;

    const sortedFiles = Array.from(droppedFiles).sort((a, b) =>
      a.name.localeCompare(b.name)
    );

    setSelectedFiles(sortedFiles);
  }, []);

  const handleDragOver = useCallback((event) => {
    event.preventDefault();
  }, []);

  const handleSaveClientDirectory = async (uploadedUrls, navbarImages) => {
    // Filter out navbar images from the uploadedUrls to ensure they're not duplicated in imageLinks
    const filteredImageLinks = uploadedUrls.filter(
      (url) => !navbarImages.includes(url)
    );

    // Sort the arrays by the file name extracted from the URL
    const sortedImageLinks = filteredImageLinks.sort((a, b) => {
      // Assuming the URL format ends with the file name
      const aName = a.split("/").pop();
      const bName = b.split("/").pop();
      return aName.localeCompare(bName);
    });

    const sortedNavbarImages = navbarImages.sort((a, b) => {
      // Assuming the URL format ends with the file name
      const aName = a.split("/").pop();
      const bName = b.split("/").pop();
      return aName.localeCompare(bName);
    });

    // Prepare the client data with the sorted arrays
    const clientData = {
      imageLinks: sortedImageLinks,
      navbarImages: sortedNavbarImages,
      imageCount: sortedImageLinks.length,
    };

    // Now, update the client in database with this new, organized data
    const response = await updateClient(clientData, client._id);
    // Handle the response (e.g., showing success or error messages)
    if (response.status === 200) {
      toast({
        title: "Success!",
        description: "Client images attached to client.",
        status: "success",
        duration: 2000,
      });
    } else {
      toast({
        title: "Error!",
        description: `Status code: ${response.status}`,
        status: "error",
        duration: 2000,
      });
    }
  };

  const checkFolderExists = async () => {
    console.log(S3_ACCESS_KEY_ID);
    AWS.config.update({
      region: AWS_REGION,
      accessKeyId: S3_ACCESS_KEY_ID,
      secretAccessKey: S3_SECRET_ACCESS_KEY,
    });

    const s3 = new AWS.S3();
    const basePrefix = folderPath + date; // e.g., 'client-creative/2023-03-24'
    const params = {
      Bucket: S3_BUCKET_NAME,
      Prefix: basePrefix,
    };

    try {
      const data = await s3.listObjectsV2(params).promise();
      const existingFolders = data.Contents.map(
        (content) => content.Key.split("/")[1]
      ).filter((key) => key.startsWith(date)); // e.g., ['2023-03-24_R1', '2023-03-24_R2']
      let maxRevision = 0; // Keep track of the highest revision number found

      existingFolders.forEach((folder) => {
        const match = folder.match(/_R(\d+)$/);
        if (match && parseInt(match[1], 10) > maxRevision) {
          maxRevision = parseInt(match[1], 10); // Update maxRevision with the higher number
        }
      });

      return `${date}_R${maxRevision + 1}`; // Return the next available folder name
    } catch (error) {
      console.log(error);
      return `${date}_R1`; // Default to R1 if there's an error or no folders
    }
  };

  const handleUpload = async () => {
    if (!selectedFiles || selectedFiles.length === 0) {
      console.error("No files selected");
      return;
    }

    // Configure AWS SDK credentials
    AWS.config.update({
      region: AWS_REGION,
      accessKeyId: S3_ACCESS_KEY_ID,
      secretAccessKey: S3_SECRET_ACCESS_KEY,
    });
    const s3 = new AWS.S3();
    // Array to hold URLs of navbar images
    let navbarImages = [];

    try {
      const folderSuffix = await checkFolderExists(); // Get the correct folder name with suffix

      const uploadPromises = selectedFiles.map((file) => {
        const img = new Image();
        img.src = URL.createObjectURL(file);

        return new Promise((resolve, reject) => {
          img.onload = () => {
            // Check if image is for navbar based on height
            const isNavbarImage = img.height < 50;

            const params = {
              Bucket: S3_BUCKET_NAME,
              Key: `${folderPath}${folderSuffix}/${file.name}`,
              Body: file,
              ContentType: file.type,
            };

            s3.upload(params)
              .promise()
              .then((result) => {
                // If the image is a navbar image, add its URL to the navbarImages array
                if (isNavbarImage) {
                  navbarImages.push(result.Location);
                }
                // Resolve the promise with the image URL
                resolve(result.Location);
              })
              .catch((error) => reject(error));
          };
          img.onerror = reject;
        });
      });

      const uploadedUrls = await Promise.all(uploadPromises);
      handleSaveClientDirectory(uploadedUrls, navbarImages);

      // Clear selected files after upload
      setSelectedFiles([]);
      setFolderPath("Please Select a Client");
      setValue(null);
    } catch (error) {
      console.error("Upload error:", error);
    }
  };

  const handleOnClick = (value) => {
    setValue((prevState) => value);
    // Check if value is truthy and has a label property
    if (value && value.label) {
      let client = clients.filter((entry) =>
        Object.values(entry).some(
          (val) => typeof val === "string" && val.includes(value.label)
        )
      );
      setClient(client[0]);
      let path = clients.filter((entry) =>
        Object.values(entry).some(
          (val) => typeof val === "string" && val.includes(value.label)
        )
      );
      if (path.length > 0) {
        setFolderPath(path[0].s3FolderPath);
      } else {
        console.log("No path found for this client");
        setFolderPath("Please Select a Client"); // Set folderPath to a default value or handle the case where no path is found
      }
    } else {
      console.log("Value is null or doesn't have a label property");
      setFolderPath("Please Select a Client");
      setSelectedFiles([]);
      // Handle the case where value is null or doesn't have a label property
    }
  };

  return (
    <Flex direction="column" pt={{ base: "150px", lg: "75px" }}>
      <Card mt="24px" bg={bgColor} withBorder>
        <CardBody>
          <Box bg={bgCard} p={"8px"} mb={"5px"} borderRadius={"4px"}>
            <Select
              isClearable={() => setValue(null)}
              isDisabled={isLoading}
              isLoading={isLoading}
              onChange={(newValue) => handleOnClick(newValue)}
              options={newOption}
              value={value}
              styles={colorStyles}
              placeholder="Type or Select a Title..."
            />
          </Box>
          <Box pb={5}>
            <InputGroup>
              <InputLeftAddon children="S3 Directory" />
              <Input
                type="text"
                color={"black"}
                disabled
                placeholder={folderPath}
              />
            </InputGroup>
          </Box>
          <Box style={{ width: "100%" }}>
            <Box
              bg={useColorModeValue("gray.200", "#4A5058")}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              color={useColorModeValue("gray.600", "white")}
              style={{
                width: "100%",
                height: "200px",
                border: "1px solid",
                borderColor: "#d0dcfb",
                borderRadius: "8px",
                textAlign: "center",
                lineHeight: "200px",
                marginBottom: "20px",
              }}
            >
              <Box>
                <Button
                  mr={10}
                  disabled={value === null ? true : false}
                  onClick={() =>
                    document.querySelector('input[type="file"]').click()
                  }
                >
                  Drag & Drop a file here or click to select
                </Button>
                Files Selected: {selectedFiles.length}
              </Box>
            </Box>
            <input
              type="file"
              multiple
              disabled={value === null ? true : false}
              onChange={handleFileChange}
              style={{ display: "none" }}
            />
            <Flex justify={"flex-end"}>
              <Button
                colorScheme="blue"
                onClick={() => handleUpload(selectedFiles)}
                isLoading={selectedFiles.length > 0 ? false : true}
                spinner={<BeatLoader size={8} color="white" />}
              >
                Upload
              </Button>
            </Flex>
          </Box>
        </CardBody>
      </Card>
    </Flex>
  );
}

export default S3Upload;
